import React, { useMemo, useRef, useState, useEffect } from 'react'
import SwiperCore, { Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useWindowResize } from 'beautiful-react-hooks'
import get from 'lodash/get'
import delay from 'lodash/delay'
import styled from 'styled-components'
import VideoPlayer from '../VideoPlayer'
import { prismicImg } from '../../utils/prismicImage'
import { isVimeoFileUrl, isVimeoPageUrl, vimeoIdFromLink } from '../../utils/helpers'
import 'swiper/swiper.scss'

const StyledSwiper = styled.div`
  .swiper-pagination {
    text-align: right;
    margin-top: 4px;
  }
  .swiper-pagination-current {
    margin-right: -4px;
  }
  .swiper-pagination-total {
    margin-left: -4px;
  }
  & + .project-info {
    margin-top: -16px;
    position: relative;
    z-index: 99;
  }

  .carousel-slide {
    cursor: pointer;
  }

  .carousel-video-wrap {
    background: #101011;
    display: grid;
    position: relative;
    width: 100%;
  }

  .carousel-video-wrap > img,
  .carousel-video-wrap > video {
    grid-area: 1 / 1;
  }

  .carousel-video-wrap > img {
    z-index: 1;
  }

  .carousel-video-wrap > video {
    opacity: 0;
    transition: opacity 0.25s ease;
    z-index: 2;
  }

  .carousel-video-wrap > video.is-ready {
    opacity: 1;
  }

  img,
  video {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Robust Vimeo sizing */
  .vimeo-wrap {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 */
    overflow: hidden;
  }
  .vimeo-wrap > .vimeo-iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }
  .vimeo-iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`

const pickUrl = (...candidates) => {
  for (const c of candidates) {
    if (!c) continue
    if (typeof c === 'string' && c.trim()) return c.trim()
    const u = get(c, 'url', '')
    if (typeof u === 'string' && u.trim()) return u.trim()
  }
  return ''
}

const getImageForPlayer = (image, fallbackAlt = '') => {
  const url = pickUrl(image)
  if (!url) return null

  if (image && typeof image === 'object') {
    return {
      ...image,
      url: prismicImg.carousel(url),
      alt: get(image, 'alt', fallbackAlt) || fallbackAlt,
    }
  }

  return {
    url: prismicImg.carousel(url),
    alt: fallbackAlt,
  }
}

const normalizeSlide = (s) => {
  const videoLinkUrl = pickUrl(
    get(s, 'video_link'),
    get(s, 'videoLink'),
    get(s, 'vimeo_link'),
    get(s, 'vimeoLink'),
    get(s, 'video_url'),
    get(s, 'videoUrl'),
  )

  const videoFileUrl = pickUrl(
    get(s, 'video_file'),
    get(s, 'videoFile'),
    get(s, 'mp4'),
    get(s, 'mp4_file'),
    get(s, 'mp4File'),
  )

  const image = get(s, 'image') || get(s, 'img')
  const imageUrl = pickUrl(image)
  const imageAlt =
    pickUrl(get(s, 'image')) ? get(s, 'image.alt', '') : get(s, 'alt', '')

  const vimeoLoop = !!get(s, 'vimeo_loop', false) || !!get(s, 'loop', false)
  const legacyUrl = pickUrl(get(s, 'url'))

  const directVideoUrl = videoFileUrl || (isVimeoFileUrl(videoLinkUrl) ? videoLinkUrl : '')
  const url = directVideoUrl || videoLinkUrl || imageUrl || legacyUrl
  if (!url) return null

  let type = get(s, 'type', null)
  if (!type) {
    if (directVideoUrl || isVimeoFileUrl(url)) type = 'video'
    else if (isVimeoPageUrl(url)) type = 'vimeo'
    else type = 'image'
  }

  const vimeoId =
    type === 'video'
      ? vimeoIdFromLink(videoLinkUrl || videoFileUrl || url)
      : isVimeoPageUrl(url) ? vimeoIdFromLink(url) : null
  if (type === 'vimeo' && !vimeoId) {
    if (imageUrl) return { type: 'image', url: imageUrl, alt: imageAlt || '', vimeoId: null }
    return null
  }

  const poster = get(s, 'poster') || image
  const posterUrl = pickUrl(poster) || imageUrl

  return {
    type,
    url,
    alt: imageAlt || get(s, 'alt', '') || '',
    vimeoId,
    vimeoLoop,
    poster,
    posterUrl,
  }
}

const looksLikePrismicRepeatItem = (obj) => {
  if (!obj || typeof obj !== 'object') return false
  return (
    !!get(obj, 'video_link.url') ||
    !!get(obj, 'video_file.url') ||
    !!get(obj, 'image.url') ||
    !!get(obj, 'video_link') ||
    !!get(obj, 'video_file') ||
    !!get(obj, 'image')
  )
}

// Safe helper for modulo window calculations
const mod = (n, m) => ((n % m) + m) % m

export default function Carousel({
  images,
  slides,
  onResize = () => {},
  playerTheme = 'blue',

  // NEW knobs:
  vimeoPreloadRadius = 1,   // 1 = mount prev + active + next
  warmAllVimeoOnIdle = false, // true = eventually mount all vimeo (heaviest)
}) {
  SwiperCore.use([Pagination])

  const swiperRef = useRef(null)

  const [activeIndex, setActiveIndex] = useState(0)
  const [warmedAllVimeo, setWarmedAllVimeo] = useState(false)
  const normalizedSlides = useMemo(() => {
    if (Array.isArray(slides) && slides.length) {
      return slides.map(normalizeSlide).filter(Boolean)
    }

    if (Array.isArray(images) && images.length) {
      const first = images[0]
      if (looksLikePrismicRepeatItem(first)) {
        return images.map(normalizeSlide).filter(Boolean)
      }

      return images
        .map((img) =>
          normalizeSlide({
            image: { url: get(img, 'url', ''), alt: get(img, 'alt', '') },
          }),
        )
        .filter(Boolean)
    }

    return []
  }, [slides, images])

  useWindowResize(() => onResize())

  useEffect(() => {
    if (!warmAllVimeoOnIdle) return
    if (warmedAllVimeo) return
    if (!normalizedSlides.length) return

    const hasVimeo = normalizedSlides.some((s) => s.type === 'vimeo' && s.vimeoId)
    if (!hasVimeo) return

    const cb = () => setWarmedAllVimeo(true)

    // Warm after initial paint / when browser is idle
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(cb, { timeout: 2500 })
      return () => window.cancelIdleCallback?.(id)
    }

    const t = setTimeout(cb, 1200)
    return () => clearTimeout(t)
  }, [warmAllVimeoOnIdle, warmedAllVimeo, normalizedSlides])

  if (!normalizedSlides.length) return null

  const shouldMountVimeoAtIndex = (index) => {
    const n = normalizedSlides.length
    if (!n) return false
    if (warmedAllVimeo) return true

    // Only matters for vimeo slides; other types render normally.
    // Mount if within radius of active index (wraparound aware).
    if (vimeoPreloadRadius <= 0) return index === activeIndex

    const a = activeIndex
    for (let r = -vimeoPreloadRadius; r <= vimeoPreloadRadius; r++) {
      if (mod(a + r, n) === index) return true
    }
    return false
  }

  const renderVimeoSlide = (s, index) => {
    const mount = shouldMountVimeoAtIndex(index)

    if (!mount) {
      return s.posterUrl ? (
        <img
          src={prismicImg.carousel(s.posterUrl)}
          alt={s.alt || ''}
          loading="lazy"
          decoding="async"
          onLoad={onResize}
        />
      ) : (
        <div className="vimeo-wrap" />
      )
    }

    return (
      <VideoPlayer
        videoId={s.vimeoId}
        loop={!!s.vimeoLoop}
        allowMobileLoop={!!s.vimeoLoop}
        playerTheme={playerTheme}
        poster={getImageForPlayer(s.poster, s.alt || '')}
        // If your VideoPlayer forwards props to iframe, these help:
        // loading="eager"
      />
    )
  }

  const renderDirectVideoSlide = (s) => (
    <VideoPlayer
      videoId={s.vimeoId || ''}
      videoUrl={s.url}
      loop={!!s.vimeoLoop || !!s.loop}
      allowMobileLoop={!!s.vimeoLoop || !!s.loop}
      playerTheme={playerTheme}
      poster={getImageForPlayer(s.poster, s.alt || '')}
    />
  )

  return (
    <StyledSwiper className="carousel-wrapper">
      <Swiper
        spaceBetween={8}
        slidesPerView={1}
        loop={true}
        pagination={{
          el: '.swiper-pagination',
          clickable: true,
          type: 'fraction',
        }}
        onResize={(swiper) => delay(() => swiper.update(), 250)}
        onSlideChange={(swiper) => {
          const idx = swiper.realIndex || 0
          setActiveIndex(idx)
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper
          const idx = swiper.realIndex || 0
          setActiveIndex(idx)
        }}
      >
        {normalizedSlides.map((s, index) => (
          <SwiperSlide key={index}>
            <div
              className="carousel-slide"
              onClick={() => {
                if (s.type === 'vimeo') return
                const swiper = swiperRef.current
                if (swiper && typeof swiper.slideNext === 'function') swiper.slideNext()
              }}
            >
              {s.type === 'vimeo' ? (
                renderVimeoSlide(s, index)
              ) : s.type === 'video' ? (
                renderDirectVideoSlide(s)
              ) : (
                <img
                  src={prismicImg.carousel(s.url)}
                  alt={s.alt || ''}
                  loading="lazy"
                  decoding="async"
                  onLoad={onResize}
                />
              )}
            </div>
          </SwiperSlide>
        ))}
        <div className="swiper-pagination" />
      </Swiper>
    </StyledSwiper>
  )
}
